import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Input, Modal, Button, Table, Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAllUsers, sendMessage, deleteUsers } from '../../store/slices/userSlice';
import { CustomModal } from '../../components/common';
import { requiredRule } from '../../utils';
import * as constant from '../../actions/constant';
import toastr from 'toastr';

const { confirm } = Modal;
const { TextArea } = Input;

interface UserItem {
  _id: string;
  name: string;
  email: string;
  role: { rolename: string };
}

const Messaging: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const searchInput = useRef<any>(null);

  const userData = useAppSelector((state) => state.user.userData) as UserItem[];

  const [visible, setVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<UserItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isMultiple, setIsMultiple] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  useEffect(() => {
    dispatch(getAllUsers({ search_string: '', page: 0, sortValue: '', sortOrder: '' }));
  }, [dispatch]);

  const showModal = (id: string, multiple: boolean) => {
    setVisible(true);
    setIsMultiple(multiple);
    setSelectedUserId(id);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const users = isMultiple ? selectedRows.map((r) => r._id) : [selectedUserId];

      if (isMultiple && users.length === 0) {
        toastr.warning('Please select user');
        return;
      }

      dispatch(sendMessage({ title: values.messageTitle, message: values.messageDescription, userIds: users }));
      setVisible(false);
      setIsMultiple(false);
    } catch {
      // Validation failed - form will show errors
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setIsMultiple(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _showDeleteConfirm = () => {
    if (selectedRows.length === 0) {
      toastr.warning('Please select user');
      return;
    }
    confirm({
      title: constant.DELETE_RECORD,
      content: '',
      onOk: () => dispatch(deleteUsers({ userIds: selectedRows.map((r) => r._id) })),
    });
  };

  const handleSearch = (selectedKeys: string[], confirmFilter: () => void, dataIndex: string) => {
    confirmFilter();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm: confirmFilter, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirmFilter, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button type="primary" onClick={() => handleSearch(selectedKeys, confirmFilter, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value: string, record: any) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (v: boolean) => { if (v) setTimeout(() => searchInput.current?.select()); },
    render: (text: string) => (searchedColumn === dataIndex ? text : text),
  });

  const columns: any[] = useMemo(() => [
    { title: 'Name', dataIndex: 'name', sorter: (a: any, b: any) => a.name.localeCompare(b.name), sortDirections: ['descend', 'ascend'], ...getColumnSearchProps('name') },
    { title: 'Email', dataIndex: 'email', sorter: (a: any, b: any) => a.email.localeCompare(b.email), sortDirections: ['descend', 'ascend'], ...getColumnSearchProps('email') },
    {
      title: 'Action',
      dataIndex: 'action',
      className: 'action-icons',
      render: (_: any, record: any) => (
        <Button className="btn-sm mr-2" type="link" onClick={() => showModal(record._id, false)}>
          <i className="fa fa-envelope" style={{ color: '#666' }} aria-hidden="true" />
        </Button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [searchedColumn]);

  const tableData = useMemo(() =>
    userData
      .filter((u) => u.role?.rolename === 'user' || u.role?.rolename === 'kingbot')
      .map((u, idx) => ({ _id: u._id, key: idx, name: u.name, email: u.email, rolename: u.role.rolename })),
    [userData]
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: any[]) => { setSelectedRowKeys(keys); setSelectedRows(rows); },
    hideDefaultSelections: true,
    selections: [{ key: 'all-data', text: 'Select All Data', onSelect: () => setSelectedRowKeys([...Array(tableData.length).keys()]) }],
  };

  return (
    <>
      <h2 className="page-heading">Messaging</h2>
      <div className="box box-default mb-4">
        <div className="box-body">
          <Row className="mb-4">
            <Col sm={6}>
              <Input prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} placeholder="Search" style={{ width: '100%' }} className="mobile-bottom-space" />
            </Col>
            <Col sm={6} className="text-right">
              <Button onClick={() => showModal('', true)} type="default">
                <i className="fa fa-paper-plane mr-3" style={{ color: '#666' }} aria-hidden="true" />
                Send Message
              </Button>
            </Col>
          </Row>
          <div className="table-responsive">
            <Table className="table" rowSelection={rowSelection} columns={columns} dataSource={tableData} />
          </div>
          <CustomModal handleCancel={handleCancel} handleOk={handleOk} visible={visible} title="Send Message" backBtnText="Cancel" submitBtnText="Send Message">
            <Form form={form} className="team-form l_hgt0" layout="vertical">
              <Form.Item label="Message Title" name="messageTitle" rules={[requiredRule('title')]}>
                <Input placeholder="Title" />
              </Form.Item>
              <Form.Item label="Message Description" name="messageDescription" rules={[requiredRule('description')]}>
                <TextArea placeholder="Description" />
              </Form.Item>
            </Form>
          </CustomModal>
        </div>
      </div>
    </>
  );
};

export default Messaging;



